import { Box, Theme, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts/highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AttentionIcon } from '../../../assets/icons/attention.svg';
import { ReactComponent as CheckmarkFilledIcon } from '../../../assets/icons/checkmarkFilled.svg';
import StarIconPath from '../../../assets/images/pdf/star.svg';
import { GoalPriorityEnum } from '../../../common/types';
import { formatCurrency, getPriorityLabel } from '../../../utils';

if (typeof Highcharts === 'object') {
    highchartsMore(Highcharts);
    solidGauge(Highcharts);
}

const SectionWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 24px;
`;

const ChartWrapper = styled(Box)`
    display: flex;
    justify-content: flex-start;
`;

export const AlertSection = styled(Box)`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const StyleDiv = styled('div')({
    '& .highcharts-container': {
        overflow: 'visible!important'
    },
    '& > div': {
        overflow: 'visible!important'
    }
});

export const AlertBanner = styled(Box, { shouldForwardProp: (prop) => prop !== '$color' })<{
    $color?: string;
}>(
    ({ $color }) => `
		display: flex;
		align-items: center;
		gap: 8px;
		color: ${$color};
		font-size: 14px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	`
);

export const AlertBannerLine = styled('span', { shouldForwardProp: (prop) => prop !== '$color' })<{
    $color?: string;
}>(
    ({ $color }) => `
		width: 2px;
		border-radius: 999px;
		background-color: ${$color};
		display: block;
		align-self: stretch;
		min-height: 54px;
	`
);

export const Description = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    lineHeight: '24px',
    color: theme.palette.semantic.text.default,
    fontWeight: 500
}));

const Highlight = styled('span')(({ theme }) => ({
    'background-color': theme.palette.semantic.bg.info,
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 600,
    color: theme.palette.semantic.text.default,
    display: 'inline-block'
}));

const clamp = (value: number) => Math.max(0, Math.min(100, value));

interface ExtendedChart extends Highcharts.Chart {
    targetBadge?: Highcharts.SVGElement;
    targetBadgeText?: Highcharts.SVGElement;
    targetLabel?: Highcharts.SVGElement;
    tickGroup?: Highcharts.SVGElement & { toBack?: () => void };
    pivotRing?: Highcharts.SVGElement;
    pivotInner?: Highcharts.SVGElement;
}

const GaugeChart = ({ value, target, color }: { value: number; target: number; color: string }) => {
    const chartRef = useRef<HighchartsReactRefObject>(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const safeValue = clamp(value);
    const safeTarget = clamp(target);

    const options = useMemo<Highcharts.Options>(() => {
        const baseBand: Highcharts.YAxisPlotBandsOptions = {
            from: 0,
            to: 100,
            color: theme.palette.neutral[200],
            outerRadius: '110%',
            innerRadius: '80%',
            thickness: '15%',
            borderWidth: 0,
            zIndex: 1
        };

        const currentBand: Highcharts.YAxisPlotBandsOptions = {
            from: 0,
            to: safeValue,
            color: {
                linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
                stops: [
                    [0, color],
                    [1, color]
                ]
            },
            outerRadius: '110%',
            innerRadius: '80%',
            thickness: '15%',
            borderWidth: 0,
            zIndex: 1
        };

        // Add a white band for the remaining 0.5
        const whiteBand: Highcharts.YAxisPlotBandsOptions = {
            from: safeValue,
            to: safeValue + 0.5,
            color: theme.palette.neutral.white,
            outerRadius: '110%',
            innerRadius: '80%',
            thickness: '15%',
            borderWidth: 0,
            zIndex: 2
        };

        const targetBand: Highcharts.YAxisPlotBandsOptions | null =
            safeTarget > safeValue
                ? {
                      from: safeValue,
                      to: safeTarget - 0.5,
                      color: theme.palette.neutral[200],
                      outerRadius: '110%',
                      innerRadius: '80%',
                      thickness: '15%',
                      borderWidth: 0,
                      zIndex: 2,
                      borderColor: theme.palette.neutral[200]
                  }
                : {
                      from: safeTarget,
                      to: safeValue,
                      color: {
                          linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
                          stops: [
                              [0, color],
                              [1, color]
                          ]
                      },
                      outerRadius: '110%',
                      innerRadius: '80%',
                      thickness: '15%',
                      borderWidth: 0,
                      zIndex: 2
                  };

        // Add a white band for the remaining 5
        const whiteBand1: Highcharts.YAxisPlotBandsOptions = {
            from: safeTarget - 0.5,
            to: safeTarget,
            color: theme.palette.neutral.white,
            outerRadius: '110%',
            innerRadius: '80%',
            thickness: '15%',
            borderWidth: 0,
            zIndex: 2
        };

        const currentLabelHtml = `
			<div style="display:flex;flex-direction:row;gap:6px;align-items:center;justify-content:center;margin-top:18px;">
				<span style="color:${color};font-family:'TT Commons Pro';font-size:13px;font-weight:500;line-height:18px;font-variant-numeric:lining-nums tabular-nums;">${t(
            'PROBABILITY_GAUGE_CURRENT_LABEL'
        )}</span>
				<span style="color:${color};font-family:'TT Commons Pro';font-size:20px;font-weight:500;line-height:24px;font-variant-numeric:lining-nums tabular-nums;">${safeValue}%</span>
			</div>
		`;

        return {
            chart: {
                type: 'gauge',
                backgroundColor: 'transparent',
                height: 200,
                width: 360,
                overflow: 'visible',
                spacing: [0, 0, 0, 0],
                style: {
                    fontFamily: 'TT Commons Pro, sans-serif'
                }
            },
            title: { text: '' },
            credits: { enabled: false },
            tooltip: { enabled: false },
            pane: {
                startAngle: -110,
                endAngle: 110,
                center: ['50%', '75%'],
                size: '95%',
                background: []
            },
            yAxis: {
                min: 0,
                max: 100,
                lineWidth: 0,
                gridLineWidth: 0,
                minorGridLineWidth: 0,
                tickWidth: 0,
                minorTickLength: 0,
                tickLength: 0,
                labels: { enabled: false },
                plotBands: targetBand
                    ? [baseBand, targetBand, currentBand, whiteBand, whiteBand1].filter(Boolean)
                    : [baseBand, currentBand, whiteBand, ...(safeTarget > safeValue ? [whiteBand1] : [])]
            },
            series: [
                {
                    type: 'gauge',
                    name: 'Probability',
                    data: [safeValue],
                    dataLabels: {
                        useHTML: true,
                        borderWidth: 0,
                        format: currentLabelHtml,
                        y: 55
                    },
                    dial: {
                        backgroundColor: theme.palette.semantic.bg.brandDark,
                        baseLength: '0%',
                        rearLength: '0%',
                        baseWidth: 6,
                        topWidth: 1,
                        radius: '90%'
                    },
                    pivot: {
                        backgroundColor: theme.palette.neutral.white,
                        radius: 2,
                        borderColor: theme.palette.semantic.bg.brandDark,
                        borderWidth: 2
                    }
                }
            ]
        };
    }, [safeValue, safeTarget, color, t]);

    useEffect(() => {
        const instance = chartRef.current as HighchartsReactRefObject | null;
        const chart = instance?.chart as ExtendedChart | undefined;
        if (!chart) {
            return;
        }

        const updateDecorations = () => {
            const axis = chart.yAxis?.[0] as
                | (Highcharts.Axis & {
                      startAngleRad: number;
                      endAngleRad: number;
                      center?: [number, number, number, number];
                  })
                | undefined;
            if (!axis || !axis.center) {
                return;
            }

            const { startAngleRad, endAngleRad, center } = axis;
            const paneRadius = center[2] / 2;
            const axisRange = endAngleRad - startAngleRad;
            const radius = paneRadius * 0.92;
            // const bandInnerRadius = paneRadius * 0.95;
            // const tickOuterRadius = bandInnerRadius - 4;
            const centerX = chart.plotLeft + center[0];
            const centerY = chart.plotTop + center[1];
            const toRad = (val: number) => startAngleRad + (axisRange * val) / 100;
            const targetAngleRad = toRad(safeTarget);
            const badgeDistance = radius + 2;
            const badgeRadius = 8;
            const badgeX = centerX + Math.cos(targetAngleRad) * badgeDistance;
            const badgeY = centerY + Math.sin(targetAngleRad) * badgeDistance;

            if (chart.tickGroup) {
                chart.tickGroup.destroy();
            }
            const tickGroupParent = chart.renderer.boxWrapper || chart.renderer.box;
            chart.tickGroup = chart.renderer
                .g('pos-gauge-ticks')
                .attr({ zIndex: -20, style: 'display: none;' })
                .add(tickGroupParent);
            chart.tickGroup?.toBack?.();

            // const drawTick = (angleRad: number, innerR: number, outerR: number, color: string, width: number) => {
            //     const x1 = centerX + Math.cos(angleRad) * innerR;
            //     const y1 = centerY + Math.sin(angleRad) * innerR;
            //     const x2 = centerX + Math.cos(angleRad) * outerR;
            //     const y2 = centerY + Math.sin(angleRad) * outerR;
            //     const tickPath: Highcharts.SVGPathArray = [
            //         ['M', x1, y1],
            //         ['L', x2, y2]
            //     ];

            //     chart.renderer.path(tickPath).attr({ stroke: color, 'stroke-width': width }).add(chart.tickGroup!);
            // };

            // const majorTicks = 10;
            // const minorSegments = 4;
            // for (let i = 0; i <= majorTicks; i += 1) {
            //     const ratio = i / majorTicks;
            //     const angleRad = startAngleRad + axisRange * ratio;
            //     drawTick(angleRad, tickOuterRadius - 14, tickOuterRadius, '#3B3B3B', 1);

            //     if (i < majorTicks) {
            //         for (let m = 1; m <= minorSegments; m += 1) {
            //             const stepRatio = ratio + (m / (minorSegments + 1)) * (1 / majorTicks);
            //             const minorAngle = startAngleRad + axisRange * stepRatio;
            //             drawTick(minorAngle, tickOuterRadius - 12, tickOuterRadius - 4, '#BDBDBD', 1);
            //         }
            //     }
            // }

            const pivotInnerMissing = !chart.pivotInner || !chart.pivotInner.element;

            // if (!chart.targetBadge) {
            //     chart.targetBadge = chart.renderer
            //         .circle(badgeX, badgeY, badgeRadius)
            //         .attr({ fill: theme.palette.info[300], stroke: theme.palette.info[300], 'stroke-width': 1, zIndex: 6 })
            //         .add();
            // } else {
            //     chart.targetBadge.attr({ cx: badgeX, cy: badgeY });
            // }

            if (!chart.targetBadge) {
                chart.targetBadge = chart.renderer
                    .image(
                        StarIconPath, // path or URL to svg file
                        badgeX - badgeRadius,
                        badgeY - badgeRadius,
                        15,
                        15
                    )
                    .attr({ zIndex: 8 })
                    .add();
            } else {
                chart.targetBadge.attr({ cx: badgeX, cy: badgeY });
            }

            // if (!chart.targetBadgeText) {
            //     chart.targetBadgeText = chart.renderer
            //         .text('★', badgeX, badgeY + 4)
            //         .attr({ fill: theme.palette.neutral.white, zIndex: 7 })
            //         .css({ fontSize: '15px', fontWeight: '700', textAlign: 'center' })
            //         .add();
            // } else {
            //     chart.targetBadgeText.attr({ x: badgeX, y: badgeY + 4 });
            // }
            if (chart.targetBadgeText && chart.targetBadgeText.element) {
                chart.targetBadgeText.element.setAttribute('text-anchor', 'middle');
            }

            const labelHtml = `<div style="font-size:12px;font-weight:500;color:${
                theme.palette.semantic.int.selected
            };white-space:nowrap;">${t(
                'PROBABILITY_GAUGE_TARGET_LABEL'
            )} <span style="font-weight:600;">${safeTarget}%</span></div>`;

            const theta = (safeTarget * Math.PI) / 180;
            const pivot = (70 * Math.PI) / 180;

            const amplitude = 10 / Math.sin((15 * Math.PI) / 180);

            const offsetY = amplitude * Math.sin(theta - pivot);

            if (!chart.targetLabel) {
                chart.targetLabel = chart.renderer
                    .text(
                        labelHtml,
                        safeTarget > 45 ? badgeX + 25 : badgeX - 85,
                        safeTarget < 45 ? badgeY : badgeY + offsetY,
                        true
                    )
                    .attr({ zIndex: 7 })
                    .add();
            } else {
                chart.targetLabel.attr({
                    x: safeTarget > 45 ? badgeX + 25 : badgeX - 85,
                    y: safeTarget < 45 ? badgeY : badgeY + offsetY
                });
                const element = chart.targetLabel.element as HTMLElement | undefined;
                if (element) {
                    element.innerHTML = labelHtml;
                }
            }
            // if (pivotRingMissing) {
            //     chart.pivotRing = chart.renderer
            //         .circle(centerX, centerY, 14)
            //         .attr({ fill: '#FFFFFF', stroke: '#C7C7C7', 'stroke-width': 5, zIndex: 8 })
            //         .add();
            // } else {
            //     chart.pivotRing?.attr({ cx: centerX, cy: centerY });
            // }

            if (pivotInnerMissing) {
                chart.pivotInner = chart.renderer
                    .circle(centerX, centerY, 7)
                    .attr({ fill: theme.palette.semantic.bg.disabled, zIndex: 1 })
                    .add();
            } else {
                chart.pivotInner?.attr({ cx: centerX, cy: centerY });
            }
        };

        updateDecorations();
        Highcharts.addEvent(chart, 'redraw', updateDecorations);

        return () => {
            Highcharts.removeEvent(chart, 'redraw', updateDecorations);
            if (chart.targetBadge) {
                chart.targetBadge.destroy();
                chart.targetBadge = undefined;
            }
            if (chart.targetBadgeText) {
                chart.targetBadgeText.destroy();
                chart.targetBadgeText = undefined;
            }
            if (chart.targetLabel) {
                chart.targetLabel.destroy();
                chart.targetLabel = undefined;
            }
            if (chart.tickGroup) {
                chart.tickGroup.destroy();
                chart.tickGroup = undefined;
            }
            if (chart.pivotRing) {
                chart.pivotRing.destroy();
                chart.pivotRing = undefined;
            }
            if (chart.pivotInner) {
                chart.pivotInner.destroy();
                chart.pivotInner = undefined;
            }
        };
    }, [safeTarget]);

    return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />;
};

const getColorByVariant = (currentValue: number, thresholdValue: number, theme: Theme) => {
    // if (currentValue < dangerValue) return theme.palette.berry[400]; // remove unrealistic scenario ref: AEX-2993
    if (currentValue < thresholdValue) return theme.palette.orange[300];
    return theme.palette.teal[300];
};

type Props = {
    value: number;
    target: number;
    thresholdValue: number;
    dangerValue?: number;
    goalType: GoalPriorityEnum;
    isPDF?: boolean;
    isGoalRealistic?: boolean;
    targetGoalAmount?: number;
    probableGoalAmount?: number;
    isRetirement?: boolean;
    isMonthlyIncome?: boolean;
    tenant?: string;
};

export const ProbabilityOfSuccessGauge = ({
    value = 0,
    target = 0,
    thresholdValue = 0,
    goalType,
    targetGoalAmount = 0,
    probableGoalAmount = 0,
    isRetirement,
    isMonthlyIncome = false,
    tenant
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const color = getColorByVariant(value, thresholdValue, theme);
    const isOnTarget = value >= thresholdValue;
    const showProbableLine = !isRetirement && value !== target;

    return (
        <SectionWrapper>
            <ChartWrapper>
                <StyleDiv>
                    <GaugeChart value={value} target={target} color={color} />
                </StyleDiv>
            </ChartWrapper>

            <AlertSection>
                <AlertBannerLine $color={color} />
                <Box>
                    <AlertBanner $color={color}>
                        {isOnTarget ? (
                            <CheckmarkFilledIcon width={18} height={18} style={{ marginRight: '8px' }} />
                        ) : (
                            <AttentionIcon width={18} height={18} color={color} style={{ marginRight: '8px' }} />
                        )}
                        <span>{t(isOnTarget ? 'PLAN_IS_ON_TARGET' : 'PLAN_NEEDS_ATTENTION')}</span>
                    </AlertBanner>
                    <Description sx={{ marginTop: 1 }}>
                        {t(
                            isOnTarget
                                ? 'PROBABILITY_GAUGE_MIN_PROBABILITY_FUNDED_TEXT'
                                : 'PROBABILITY_GAUGE_MIN_PROBABILITY_UNDERFUNDED_TEXT',
                            { goalType: getPriorityLabel({ value: goalType, label: t(goalType.toUpperCase()) }, tenant, t) }
                        )}{' '}
                        <strong>{t('PROBABILITY_GAUGE_PERCENT_VALUE', { value: target })}.</strong>
                    </Description>
                </Box>
            </AlertSection>

            <Description>
                {/* {t('PROBABILITY_GAUGE_ON_TARGET_DATE')} <br /> */}
                <Highlight>{t('PROBABILITY_GAUGE_PERCENT_VALUE', { value })}</Highlight>{' '}
                {t(
                    isRetirement
                        ? isMonthlyIncome
                            ? 'PROBABILITY_GAUGE_CHANCE_TARGET_INCOME_RETIREMENT_MONTHLY'
                            : 'PROBABILITY_GAUGE_CHANCE_TARGET_INCOME_RETIREMENT'
                        : 'PROBABILITY_GAUGE_CHANCE_TARGET_INCOME_WEALTH'
                )}{' '}
                <Highlight>{formatCurrency(targetGoalAmount)}</Highlight>
                {showProbableLine && (
                    <>
                        <br />
                        <Highlight>{t('PROBABILITY_GAUGE_PERCENT_VALUE', { value: target })}</Highlight>{' '}
                        {t(
                            isRetirement
                                ? 'PROBABILITY_GAUGE_CHANCE_ANNUAL_INCOME_RETIREMENT'
                                : 'PROBABILITY_GAUGE_CHANCE_ANNUAL_INCOME_WEALTH'
                        )}{' '}
                        <Highlight>{formatCurrency(probableGoalAmount)}</Highlight>
                        {probableGoalAmount > targetGoalAmount ? (
                            <>
                                {' '}
                                {t('PROBABILITY_GAUGE_EXCESS')}{' '}
                                <Highlight>{formatCurrency(probableGoalAmount - targetGoalAmount)}</Highlight>
                            </>
                        ) : null}
                        {probableGoalAmount < targetGoalAmount ? (
                            <>
                                {' '}
                                {t('PROBABILITY_GAUGE_SHORTFALL')}{' '}
                                <Highlight>{formatCurrency(Math.abs(probableGoalAmount - targetGoalAmount))}</Highlight>
                            </>
                        ) : null}
                    </>
                )}
            </Description>
        </SectionWrapper>
    );
};
export default ProbabilityOfSuccessGauge;
